// App-wide constants. Keep small and stable.

import type {
  ExpenseCategory,
  FundingType,
  MaintenanceCategory,
  MaintenancePriority,
  TransportDestination,
} from "@/types/database";

export const APP_NAME = "Nestd";
export const APP_TAGLINE = "The Operating System for Student Accommodation in Africa";
export const AI_NAME = "Nesta";

// =====================================================================
// Demo-tenant fallback IDs.
// Used when the AuthContext / profile lookup can't return a real value.
// Single-tenant only — replace with proper resolution before adding a
// second sVillage property or a second company.
// =====================================================================
export const FALLBACK_COMPANY_ID = "20929c77-1022-47fc-8dc4-72e63d791cc0";
export const FALLBACK_PROPERTY_ID = "313164b2-eb19-4270-9242-4409e7643bdc";

export const ROLE_HOME: Record<string, string> = {
  super_admin: "/hq/dashboard",
  owner: "/owner/dashboard",
  admin: "/admin/dashboard",
  staff: "/staff/home",
  student: "/student/home",
};

export const TRANSPORT_DESTINATIONS: Array<{
  value: TransportDestination;
  label: string;
  icon: string;
}> = [
  { value: "tut_main", label: "TUT Main Campus", icon: "🎓" },
  { value: "tshwane_south", label: "Tshwane South College", icon: "🏫" },
  { value: "tshwane_north", label: "Tshwane North College", icon: "🏫" },
];

// =====================================================================
// Academic campuses — the campus a resident physically studies at.
// Stored as free TEXT on students.campus (migration 022), so this list is
// just convenience suggestions; admins/applicants can type any campus.
// Seeded with Tshwane University of Technology (TUT) campuses for M House.
// Add new institutions' campuses here — no DB migration required.
// =====================================================================
export const CAMPUS_OPTIONS: string[] = [
  "Pretoria (Main)",
  "Arcadia",
  "Arts",
  "Soshanguve South",
  "Soshanguve North",
  "Ga-Rankuwa",
  "eMalahleni",
  "Mbombela",
  "Polokwane",
  "Other",
];

export const QUICK_PICKUP_TIMES = [
  "06:00",
  "06:30",
  "06:45",
  "07:00",
  "07:30",
  "08:00",
];

export const MAINTENANCE_CATEGORIES: Array<{
  value: MaintenanceCategory;
  label: string;
}> = [
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "wifi", label: "Wi-Fi" },
  { value: "appliance", label: "Appliance" },
  { value: "structural", label: "Structural" },
  { value: "pest", label: "Pest" },
  { value: "cleaning", label: "Cleaning" },
  { value: "other", label: "Other" },
];

// =====================================================================
// Funding type — Africa-wide labels.
// `nsfas` is kept for backwards compatibility with already-seeded
// students; new applicants pick from FUNDING_TYPE_OPTIONS below, which
// excludes the legacy value. FUNDING_TYPE_LABELS covers both old and
// new values so display sites render consistently regardless of which
// value a row holds.
// =====================================================================
export const FUNDING_TYPE_LABELS: Record<FundingType, string> = {
  // Legacy value — surfaced when reading old rows
  nsfas: "Government Funded (NSFAS)",
  // Active values
  government_funded: "Government Funded",
  self_funded: "Self Funded",
  bursary: "Bursary / Scholarship",
  employer_sponsored: "Employer Sponsored",
  family_sponsored: "Family Sponsored",
  student_loan: "Student Loan",
};

// Options shown to new applicants in the dropdown — note the omission
// of the legacy `nsfas` value.
export const FUNDING_TYPE_OPTIONS: Array<{ value: FundingType; label: string }> = [
  { value: "government_funded", label: FUNDING_TYPE_LABELS.government_funded },
  { value: "self_funded", label: FUNDING_TYPE_LABELS.self_funded },
  { value: "bursary", label: FUNDING_TYPE_LABELS.bursary },
  { value: "employer_sponsored", label: FUNDING_TYPE_LABELS.employer_sponsored },
  { value: "family_sponsored", label: FUNDING_TYPE_LABELS.family_sponsored },
  { value: "student_loan", label: FUNDING_TYPE_LABELS.student_loan },
];

export const MAINTENANCE_PRIORITIES: Array<{
  value: MaintenancePriority;
  label: string;
}> = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export const EXPENSE_CATEGORIES: Array<{
  value: ExpenseCategory;
  label: string;
}> = [
  { value: "plumbing", label: "Plumbing" },
  { value: "electrical", label: "Electrical" },
  { value: "cleaning", label: "Cleaning" },
  { value: "security", label: "Security" },
  { value: "maintenance", label: "Maintenance" },
  { value: "garden", label: "Garden" },
  { value: "transport", label: "Transport" },
  { value: "supplies", label: "Supplies" },
  { value: "admin", label: "Admin" },
];

export const EXPENSE_CATEGORY_PALETTE: Record<string, string> = {
  plumbing: "#4A8FC0",
  electrical: "#E8A020",
  cleaning: "#4CAF7C",
  security: "#C85050",
  maintenance: "#C9A84C",
  garden: "#7AB87E",
  transport: "#9D7BC0",
  supplies: "#888070",
  admin: "#444038",
};

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
